import React, { useEffect, useState, useContext } from 'react';
import * as THREE from 'three';
import SideMenu from '@src/components/menu/sideMenu';
import SideDrawer from '@src/components/menu/sideDrawer';
import EditorModalGroup from './EditorModalGroup';
import {
  useMFesTemplatesQuery,
  useMfeSceneTemplatesQuery,
  useDeleteLightPropsMutation,
} from '@src/components/generated/graphql';
import useSafeUseProgress from '@src/components/hooks/useSafeProgress';
import { useBranding } from '@src/theme/branding/useBranding';
import { makeId } from 'util/helpers';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { SceneAsset } from '@src/components/scene/react-three-components/sceneAsset';
import { zepetoScenes } from '../../zepeto-scenes';
import { eventSideDrawer } from '../lib/event-side-drawer';
import { S3_BUCKET, CDN } from '@src/components/scene/assets/baseUrl';
import { positionalAudiosPack } from '@src/components/scene/assets/positionalAudiosPack';
import { videosPack } from '@src/components/scene/assets/videosPack';
import { imagesPack } from '@src/components/scene/assets/imagesPack';
import { getCameraRelativePosition } from 'util/helpers';

const blankSceneTemplateURL = `${CDN}/studioAssets/imageAssets/noSceneT.jpg`;

export default function EditorMenu({
  user,
  isAddedInitialPoint,
  setIsAddedInitialPoint,
  setSelectableAssets,
  selectedModel,
  deleteSelectedModel,
  assetsToDelete,
  setAssetsToDelete,
  setSelectedModel,
  setIsTransformControlsActive,
  sceneTemplates,
  buildScene,
  skyBoxIn,
  lightsIn,
  camera,
  cameraInitialPosition,
  selectableAssets,
  loadSceneTemplate,
  setCurrentCameraConfig,
  setSelectedSkybox,
  setLightTemplate,
  isTransformControlsActive,
  tourRefs,
  setIsTourOpen,
  isEditorDrawerOpen,
  currentCameraConfig,
  selectObject,
  lightTemplate,
  selectedSkybox,
  selectedSceneTemplate,
  setIsEnableShadowLight,
  saveCameraConfig,
  saveScene,
  isOpenWorlds,
  setIsOpenWorlds,
  sceneId,
  setIsPortalModalOpen,
  isLinkModalOpen,
  setIsLinkModalOpen,
  isPOIModalOpen,
  setIsPOIModalOpen,
  isPortalModalOpen,
  scene,
}) {
  const { isZepeto } = useBranding();
  const [dataSideDrawer, setDataSideDrawer] = useState<any>({
    elements: [],
    type: '',
  });
  const { safeActive: active } = useSafeUseProgress();
  const [deleteLightProps] = useDeleteLightPropsMutation();
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState<boolean>(false);
  const [MFESTemplatesScenes, setMFESTemplatesScenes] = useState<any>([]);
  const [isAssetStoreOpen, setIsAssetStoreOpen] = useState(false);
  const [isMyStuffOpen, setIsMyStuffOpen] = useState(false);
  const [isGenerateImageOpen, setIsGenerateImageOpen] = useState(false);
  const [zepetoKey, setZepetoKey] = useState<any>();

  const {
    data: mfeTemplates,
    loading: l3,
    error: e3,
  } = useMFesTemplatesQuery();
  const {
    data: MFESScenes,
    loading: l4,
    error: e4,
  } = useMfeSceneTemplatesQuery();

  const addInitialPoint = () => {
    if (!isAddedInitialPoint) {
      const position = camera
        ? getCameraRelativePosition(camera, 10)
        : new THREE.Vector3(0, 0, 10);

      const rotation = new THREE.Euler(0, 0, 0);

      const instanceId = makeId(10);
      setIsAddedInitialPoint(true);
      let assetType = {
        type: ASSET_TYPE.INITIAL_POINT,
        url: '',
        name: 'Initial-Point',
        id: '',
        ref: React.createRef(),
      };
      let sceneAsset = (
        <SceneAsset
          key={`Initial-Point-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(1, 1, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      setSelectableAssets({ type: 'add', newElement: { ...sceneAsset } });
    }
  };

  const deleteAsset = (asset) => {
    if (asset === selectedModel) {
      deleteSelectedModel();
      return null;
    }

    if (asset?.props?.asset?.name?.includes('Initial-Point')) {
      setIsAddedInitialPoint(false);
    }
    setSelectableAssets({
      type: 'delete',
      deleteElement: { ...asset },
    });

    const id = asset.props.asset.id;
    if (id) {
      if (asset?.props?.asset?.type === ASSET_TYPE.MISC.LIGHT) {
        deleteLightProps({
          variables: {
            where: {
              id: asset.props.light.id,
            },
          },
        });
      } else {
        const newSet = new Set(assetsToDelete);
        newSet.add(id);
        setAssetsToDelete(newSet);
      }
    }

    setSelectedModel(() => null);
    setIsTransformControlsActive(false);
    return null;
  };

  const closeAssetStore = () => {
    setIsAssetStoreOpen(false);
  };

  const eventSideMenu = (type) => {
    switch (type) {
      case SIDE_MENU_EVENTS.MFE_WORLDS:
        setDataSideDrawer({
          elements: MFESTemplatesScenes,
          type,
        });
        break;

      case SIDE_MENU_EVENTS.SCENES:
        if (isZepeto) {
          setDataSideDrawer({
            elements: zepetoScenes,
            type,
          });
          break;
        }
        const sceneTemplateWithBlank = [
          {
            id: '',
            baseModelURL: '',
            thumbnail: blankSceneTemplateURL,
          },
          ...(user.role === 'admin'
            ? [
                {
                  id: '',
                  baseModelURL: `${CDN}/studioAssets/MFEadmin23/CyberHub/`,
                  thumbnail: blankSceneTemplateURL,
                },
                {
                  id: '',
                  baseModelURL: `${CDN}/studioAssets/MFEadmin23/FlowHub/`,
                  thumbnail: blankSceneTemplateURL,
                },
              ]
            : []),
          ...(sceneTemplates?.sceneTemplates.map((t) => ({
            ...t,
            baseModelURL: t.baseModelURL?.replace(S3_BUCKET, CDN),
            thumbnail: t.thumbnail?.replace(S3_BUCKET, CDN),
          })) || []),
        ];
        setDataSideDrawer({
          elements: sceneTemplateWithBlank,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.LIBRARY_3D:
        setDataSideDrawer({
          elements: buildScene,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.SKY:
        setDataSideDrawer({
          elements: skyBoxIn,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.LIGHTS:
        setDataSideDrawer({
          elements: lightsIn,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.AUDIOS:
        setDataSideDrawer({
          elements: positionalAudiosPack,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.VIDEO_PLANE:
        setDataSideDrawer({
          elements: videosPack,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.IMAGE_PLANE:
        setDataSideDrawer({
          elements: imagesPack,
          type,
        });
        break;
      case SIDE_MENU_EVENTS.SVG_PLANE:
        setDataSideDrawer({
          elements: [
            {
              name: 'svg1',
              thumbnail: `${CDN}/Files/Apple_logo_black.svg.svg`,
              url: `${CDN}/Files/Apple_logo_black.svg.svg`,
            },
          ],
          type,
        });
        break;
      case SIDE_MENU_EVENTS.SCREEN_SHARE:
        setDataSideDrawer({
          elements: [
            {
              name: 'screenshareCustom',
            },
          ],
          type,
        });
        break;
      case SIDE_MENU_EVENTS.YOUTUBE:
      case SIDE_MENU_EVENTS.TWITCH:
      case SIDE_MENU_EVENTS.IFRAME:
        setDataSideDrawer({
          elements: [
            {
              name: 'streamCustom',
            },
          ],
          type,
        });
        break;
      default:
        setDataSideDrawer({
          elements: undefined,
          type,
        });
        break;
    }

    setIsSideDrawerOpen(true);
  };

  const resetPositionCamera = () => {
    // Set camera position
    camera.position.set(
      cameraInitialPosition.x,
      cameraInitialPosition.y,
      cameraInitialPosition.z
    );

    const lookAtTarget = new THREE.Vector3(
      cameraInitialPosition.x + 2, // simulate original OrbitControls offset
      cameraInitialPosition.y,
      cameraInitialPosition.z
    );

    camera.lookAt(lookAtTarget);
    camera.updateProjectionMatrix();
  };

  const onEventSideDrawer = (data) => {
    if (active) {
      return;
    }

    eventSideDrawer({
      data,
      camera,
      selectableAssets,
      loadSceneTemplate,
      resetPositionCamera,
      setCurrentCameraConfig,
      setSelectedSkybox,
      setLightTemplate,
      setSelectableAssets,
    });
  };

  useEffect(() => {
    if (mfeTemplates && MFESScenes) {
      const templatesOut = mfeTemplates.mFESTemplates.map((item) => {
        return {
          tabName: item.name,
          id: item.id,
          scenes: MFESScenes.mFESceneTemplates.filter(
            (scenesIn) => scenesIn.MFESTemplateId === item.id
          ),
        };
      });
      setMFESTemplatesScenes(() => templatesOut);
    }
  }, [mfeTemplates, MFESScenes]);

  return (
    <>
      <div className='menu-root-wrapper'>
        <SideMenu
          isAddedInitialPoint={isAddedInitialPoint}
          MFESTemplatesScenes={MFESTemplatesScenes}
          callBack={eventSideMenu}
          loadAsset={onEventSideDrawer}
          openAssetStore={() => setIsAssetStoreOpen(true)}
          openMyStuff={() => setIsMyStuffOpen(true)}
          addInitialPoint={addInitialPoint}
          setZepetoKey={setZepetoKey}
          openGenerateImage={() => setIsGenerateImageOpen(true)}
          isTransformControlsActive={isTransformControlsActive}
          tourRefs={tourRefs}
          setIsTourOpen={setIsTourOpen}
          isAdmin={user.role === 'admin'}
          sceneTemplates={sceneTemplates}
          setIsOpenWorlds={setIsOpenWorlds}
          isOpenWorlds={isOpenWorlds}
          selectedSceneTemplate={selectedSceneTemplate}
          setSelectableAssets={setSelectableAssets}
          userEmail={user.email}
          buildScene={buildScene}
          camera={camera}
        />
        <SideDrawer
          loadSceneTemplate={loadSceneTemplate}
          setLightTemplate={setLightTemplate}
          saveScene={saveScene}
          className={isEditorDrawerOpen ? 'isOpen-edit' : ''}
          isOpen={isSideDrawerOpen}
          setSelectableAssets={setSelectableAssets}
          inProgress={active}
          onClose={() => {
            setIsSideDrawerOpen(false);
          }}
          currentCameraConfig={currentCameraConfig}
          type={dataSideDrawer.type}
          items={dataSideDrawer.elements}
          selectableAssets={selectableAssets}
          callBack={onEventSideDrawer}
          setSelectedObject={selectObject}
          studioState={{
            lightTemplate,
            selectedSkybox,
            selectedSceneTemplate,
            selectedModel,
            setIsEnableShadowLight,
          }}
          camera={camera}
          callBackSave={saveCameraConfig}
          zepetoKey={zepetoKey}
          deleteAsset={deleteAsset}
          sceneId={sceneId}
          scene={scene}
        />
        <EditorModalGroup
          email={user.email}
          isAssetStoreOpen={isAssetStoreOpen}
          closeAssetStore={closeAssetStore}
          selectedModel={selectedModel}
          isLinkModalOpen={isLinkModalOpen}
          setIsLinkModalOpen={setIsLinkModalOpen}
          isPOIModalOpen={isPOIModalOpen}
          setIsPOIModalOpen={setIsPOIModalOpen}
          isPortalModalOpen={isPortalModalOpen}
          user={user}
          setIsPortalModalOpen={setIsPortalModalOpen}
          isMyStuffOpen={isMyStuffOpen}
          setIsMyStuffOpen={setIsMyStuffOpen}
          eventSideDrawer={onEventSideDrawer}
          isGenerateImageOpen={isGenerateImageOpen}
          setIsGenerateImageOpen={setIsGenerateImageOpen}
        />
      </div>
    </>
  );
}
